import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency, formatTaxRateFraction } from '@/lib/currency-utils'
import { formatEnumName } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'

function formatPreviewQuantity(quantity: number): string {
  return quantity % 1 === 0 ? `${quantity}` : quantity.toFixed(1)
}

export type QuoteBuilderPreviewLine = {
  key: string
  name: string
  amountCents: number
  quantity: number
  unitPriceCents: number
  unit: ItemUnitEnum
  salesTaxRate?: number
  isTaxable?: boolean
  salesTaxCents?: number
}

export type QuoteBuilderPreviewAddon = QuoteBuilderPreviewLine & {
  /** Pre-selected add-ons render the portal's Remove action instead of Add. */
  included?: boolean
}

export type QuoteBuilderPreviewSchedule = {
  key: string
  label: string
  amountCents: number
}

export interface QuoteBuilderPreviewProps {
  recipientFirstName: string
  title: string
  validUntilLabel?: string
  eventsSummary?: string
  lineItems: QuoteBuilderPreviewLine[]
  addonOffers: QuoteBuilderPreviewAddon[]
  subtotalCents: number
  discountAmountCents: number
  discountLabel?: string
  taxAmountCents: number
  tipCents: number
  tipLabel?: string
  totalCents: number
  schedules: QuoteBuilderPreviewSchedule[]
}

function Money({ cents, className }: { cents: number; className?: string }) {
  return <span className={cn('tabular-nums', className)}>${formatCurrency(cents)}</span>
}

/** Portal-matching qty × price / unit line, e.g. "2 × $1,215.00 / Per Event". */
export function formatQuoteBuilderPreviewUnitLine(line: {
  quantity: number
  unitPriceCents: number
  unit: ItemUnitEnum
}): string {
  const quantity = formatPreviewQuantity(line.quantity)
  const price = `$${formatCurrency(line.unitPriceCents)}`
  if (!line.unit) {
    return `${quantity} × ${price}`
  }
  return `${quantity} × ${price} / ${formatEnumName(line.unit, ItemUnitEnum)}`
}

function PreviewLineItem({ line }: { line: QuoteBuilderPreviewLine }) {
  const salesTaxCents = line.salesTaxCents ?? 0

  return (
    <li className="flex items-start justify-between gap-4 border-b border-border/40 py-2 last:border-0 last:pb-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">{line.name || 'Untitled item'}</p>
        <div className="mt-0.5 flex items-center gap-2">
          <p className="font-financial text-xs text-muted-foreground">
            {formatQuoteBuilderPreviewUnitLine(line)}
          </p>
          {line.salesTaxRate !== undefined && line.salesTaxRate > 0 && (
            <span className="text-xs text-muted-foreground">
              ({formatTaxRateFraction(line.salesTaxRate)}% tax)
            </span>
          )}
        </div>
      </div>
      <div className="flex shrink-0 flex-col items-end">
        <Money cents={line.amountCents} className="font-financial text-sm font-medium" />
        {line.isTaxable && salesTaxCents > 0 && (
          <span className="font-financial text-xs text-muted-foreground">
            + ${formatCurrency(salesTaxCents)} tax
          </span>
        )}
      </div>
    </li>
  )
}

/**
 * Read-only mirror of the customer portal quote view, driven entirely by live
 * quote-builder form state. Nothing here is interactive: the accept button is
 * decorative so it can never be focused or activated from the builder.
 */
export function QuoteBuilderPreview({
  recipientFirstName,
  title,
  validUntilLabel,
  eventsSummary,
  lineItems,
  addonOffers,
  subtotalCents,
  discountAmountCents,
  discountLabel,
  taxAmountCents,
  tipCents,
  tipLabel,
  totalCents,
  schedules,
}: QuoteBuilderPreviewProps) {
  const depositCents = schedules[0]?.amountCents ?? 0
  const acceptLabel = totalCents <= 0
    ? 'Accept Quote'
    : depositCents > 0
      ? `Accept & Pay Deposit — $${formatCurrency(depositCents)}`
      : `Accept & Pay — $${formatCurrency(totalCents)}`

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Exactly what {recipientFirstName} sees · updates as you type
        </p>
        <span className="rounded-full border border-emerald-400/40 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-emerald-700">
          LIVE
        </span>
      </div>

      <div className="rounded-2xl border bg-card p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold">{title.trim() || 'Untitled quote'}</h3>
          <span className="shrink-0 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
            Quote
          </span>
        </div>
        {validUntilLabel && (
          <p className="mt-1 text-sm text-muted-foreground">Valid until {validUntilLabel}</p>
        )}

        {eventsSummary && (
          <p className="mt-3 rounded-xl bg-muted/60 px-4 py-3 text-sm">{eventsSummary}</p>
        )}

        <div className="mt-4 rounded-lg border bg-card p-4">
          <p className="mb-3 text-sm font-medium">Items</p>
          <ul>
            {lineItems.length === 0 ? (
              <li className="text-sm text-muted-foreground">No line items yet</li>
            ) : (
              lineItems.map((line) => <PreviewLineItem key={line.key} line={line} />)
            )}
          </ul>
        </div>

        {addonOffers.length > 0 && (
          <div className="mt-4 space-y-3">
            <h3 className="text-sm font-semibold">Optional add-ons</h3>
            <ul className="space-y-2">
              {addonOffers.map((offer) => (
                <li
                  key={offer.key}
                  className="flex items-start justify-between gap-4 rounded-lg border bg-card p-3"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {offer.name || 'Untitled add-on'}
                    </p>
                    <p className="mt-0.5 font-financial text-xs text-muted-foreground tabular-nums">
                      {formatQuoteBuilderPreviewUnitLine(offer)}
                      {' — '}
                      ${formatCurrency(offer.amountCents)}
                    </p>
                  </div>
                  <span
                    aria-hidden="true"
                    className={cn(
                      'inline-flex h-8 shrink-0 items-center rounded-md px-3 text-sm font-medium',
                      offer.included
                        ? 'border bg-background'
                        : 'bg-primary text-primary-foreground',
                    )}
                  >
                    {offer.included ? 'Remove' : 'Add'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span>Subtotal</span>
            <Money cents={subtotalCents} className="font-financial" />
          </div>
          {discountAmountCents > 0 && (
            <div className="flex justify-between gap-4 text-emerald-700">
              <span>{discountLabel || 'Discount'}</span>
              <span className="font-financial tabular-nums">
                −${formatCurrency(discountAmountCents)}
              </span>
            </div>
          )}
          {taxAmountCents > 0 && (
            <div className="flex justify-between gap-4">
              <span>Tax</span>
              <Money cents={taxAmountCents} className="font-financial" />
            </div>
          )}
          {tipCents > 0 && (
            <div className="flex justify-between gap-4">
              <span>{tipLabel || 'Minimum Tip'}</span>
              <Money cents={tipCents} className="font-financial" />
            </div>
          )}
        </div>

        <div className="mt-2 flex justify-between gap-4 border-t-2 border-foreground pt-3 text-lg font-bold">
          <span>Total</span>
          <Money cents={totalCents} className="font-financial" />
        </div>

        {schedules.length > 0 && (
          <div className="mt-4 space-y-3">
            <h3 className="text-sm font-semibold">Payment Schedule</h3>
            <div className="space-y-2">
              {schedules.map((schedule) => (
                <div
                  key={schedule.key}
                  className="flex justify-between gap-4 text-sm text-muted-foreground"
                >
                  <span>{schedule.label}</span>
                  <Money cents={schedule.amountCents} className="font-medium text-foreground" />
                </div>
              ))}
            </div>
          </div>
        )}

        <div
          aria-hidden="true"
          className="mt-4 w-full rounded-lg bg-foreground px-4 py-2.5 text-center text-sm font-medium text-background"
        >
          {acceptLabel}
        </div>
      </div>
    </div>
  )
}
